"use client";

import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from "react";
import { fetchMe, getToken, PASS_EVENT, syncCookie, type PassMe } from "@/lib/apex-pass/client";
import { PassPanel } from "./PassPanel";

type Ctx = {
  me: PassMe | null;
  loading: boolean;
  refresh: () => Promise<void>;
  openPanel: (mode?: "signin" | "signup") => void;
};

const PassCtx = createContext<Ctx>({ me: null, loading: false, refresh: async () => {}, openPanel: () => {} });

export function useApexPass() {
  return useContext(PassCtx);
}

/* One Pass state for the whole store. Nothing is fetched for visitors who
 * have never signed in, so it costs first-time visitors nothing. */
export function ApexPassProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<PassMe | null>(null);
  const [loading, setLoading] = useState(false);
  const [panel, setPanel] = useState<null | "signin" | "signup">(null);

  const refresh = useCallback(async () => {
    if (!getToken()) { setMe(null); return; }
    setLoading(true);
    try { setMe(await fetchMe()); } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    syncCookie();
    void refresh();
    const onChange = () => { syncCookie(); void refresh(); };
    const onStorage = (e: StorageEvent) => { if (e.key === "apex-pass") onChange(); };
    addEventListener(PASS_EVENT, onChange);
    addEventListener("storage", onStorage);
    return () => { removeEventListener(PASS_EVENT, onChange); removeEventListener("storage", onStorage); };
  }, [refresh]);

  return (
    <PassCtx.Provider value={{ me, loading, refresh, openPanel: (m = "signin") => setPanel(m) }}>
      {children}
      {panel && <PassPanel mode={panel} onMode={setPanel} onClose={() => setPanel(null)} />}
    </PassCtx.Provider>
  );
}
